<script setup>
// Config do node "Classificar": define as categorias/intenções em que o agente
// deve enquadrar a mensagem do cliente. É compilado na instrução do agente
// (o agente classifica e usa isso pra decidir o caminho/resposta).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  data: { type: Object, required: true },
});
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const categories = computed(() => props.data.categories || []);

const updateCat = (idx, key, value) => {
  const list = categories.value.map((c, i) =>
    i === idx ? { ...c, [key]: value } : c
  );
  emit('update', { categories: list });
};
const addCat = () =>
  emit('update', {
    categories: [...categories.value, { name: '', description: '' }],
  });
const removeCat = idx =>
  emit('update', { categories: categories.value.filter((_, i) => i !== idx) });
</script>

<template>
  <div class="p-4">
    <div class="mb-1 flex items-center justify-between">
      <div class="flex items-center gap-2">
        <span class="i-lucide-layout-grid text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || t('AI_AGENTS.FLOW.CLASSIFY.TITLE') }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>
    <p class="mb-3 text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.CLASSIFY.SUBTITLE') }}
    </p>

    <label class="mb-2 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.CLASSIFY.CATEGORIES') }}
    </label>
    <div class="space-y-2">
      <div
        v-for="(cat, idx) in categories"
        :key="idx"
        class="rounded-lg border border-n-weak p-2"
      >
        <div class="mb-1 flex items-center gap-2">
          <input
            :value="cat.name"
            :placeholder="t('AI_AGENTS.FLOW.CLASSIFY.NAME_PH')"
            class="w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-sm"
            @input="updateCat(idx, 'name', $event.target.value)"
          />
          <button
            class="i-lucide-trash-2 shrink-0 text-n-ruby-9"
            @click="removeCat(idx)"
          />
        </div>
        <input
          :value="cat.description"
          :placeholder="t('AI_AGENTS.FLOW.CLASSIFY.DESC_PH')"
          class="mb-1 w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-xs"
          @input="updateCat(idx, 'description', $event.target.value)"
        />
        <input
          :value="cat.examples"
          :placeholder="t('AI_AGENTS.FLOW.CLASSIFY.EXAMPLES_PH')"
          class="w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-xs"
          @input="updateCat(idx, 'examples', $event.target.value)"
        />
      </div>
    </div>
    <button
      class="mt-2 flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
      @click="addCat"
    >
      <span class="i-lucide-plus" /> {{ t('AI_AGENTS.FLOW.CLASSIFY.ADD') }}
    </button>

    <label class="mb-1 mt-4 block text-xs font-medium text-n-slate-11">
      {{ t('AI_AGENTS.FLOW.CLASSIFY.FALLBACK') }}
    </label>
    <input
      :value="data.fallback"
      :placeholder="t('AI_AGENTS.FLOW.CLASSIFY.FALLBACK_PH')"
      class="w-full rounded-md border border-n-weak bg-n-solid-1 px-2 py-1 text-sm"
      @input="emit('update', { fallback: $event.target.value })"
    />

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
